<template>
  <nav
    class="d-flex align-items-center d-none d-md-none d-lg-flex justify-content-between"
    :class="{
      'py-3': !isLoggedIn.value || isRouteWithoutSearch.value,
      'bg-white': brandName !== 'toolcenter',
      'bg-primary-app text-white': brandName === 'toolcenter'
    }"
  >
    <div class="d-flex align-items-center col-4 col-lg-3">
      <router-link class="navbar-brand ms-5" :to="{ name: 'home' }">
        <img
          :src="headerLogo"
          class="img-fluid cursor-pointer scaleHover headerLogo"
          alt="header-logo-company"
        />
      </router-link>
    </div>

    <div class="col-4">
      <SearchHelper
        v-if="!isRouteWithoutSearch.value"
        @search="search"
        aditionalInputClass="py-2"
        aditionalMainClass="my-4"
        :placeholder="'Buscar productos por código, clave, marcas...'"
        :colValue="12"
      >
        <template #beforeInput>
          <div class="ms-3" v-if="isLoggedIn.value">
            <p class="mb-1">
              Hola, <strong>{{ onlyTwoNames }}</strong>
              <span
                class="float-end cursor-pointer me-2"
                v-if="canSelectOffice"
                @click="emit('openSelectOffice')"
              >
                <font-awesome-icon icon="fa-solid fa-location-dot" class="me-2" />
                {{ officeName }}
              </span>
              <span
                id="focusSaldoBono"
                class="float-end"
                v-if="
                  agreementState.value &&
                  (agreementData?.agreementType === 1 || !agreementData?.agreementType)
                "
              >
                <span class="me-3">Saldo de bono:</span>
                <strong class="float-end pulse-animation" id="bonoSaldo">
                  {{ remainingBalance.value < 0 ? 0 : $formatcurrency(remainingBalance.value) }}
                </strong>
              </span>
              <span
                @click="openInfoAgreement"
                class="float-end"
                v-if="agreementState.value && agreementData?.agreementType === 2"
              >
                <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2 cursor-pointer" />
                <span class="me-3">Bono compra mínima:</span>
                <strong class="float-end" id="compraMinima">
                  {{ $formatcurrency(agreementData?.agreement_minimum_purchase) }}
                </strong>
              </span>
            </p>
          </div>
          <div v-else-if="canSelectOffice">
            <p class="mb-1">
              <span class="float-end cursor-pointer me-2" @click="emit('openSelectOffice')">
                <font-awesome-icon icon="fa-solid fa-location-dot" class="me-2" />
                {{ officeName }}
              </span>
            </p>
          </div>
        </template>
      </SearchHelper>
      <div class="ms-3" v-else-if="isLoggedIn.value">
        <p class="m-0">
          Hola, <strong>{{ onlyTwoNames }}</strong>
          <span
            class="float-end cursor-pointer me-2"
            v-if="canSelectOffice"
            @click="emit('openSelectOffice')"
          >
            <font-awesome-icon icon="fa-solid fa-location-dot" class="me-2" />
            {{ officeName }}
          </span>
          <span
            id="focusSaldoBono"
            class="float-end"
            v-if="
              agreementState.value &&
              (agreementData?.agreementType === 1 || !agreementData?.agreementType)
            "
          >
            <span class="me-3">Saldo de bono:</span>
            <strong class="float-end pulse-animation">
              {{ remainingBalance.value < 0 ? 0 : $formatcurrency(remainingBalance.value) }}
            </strong>
          </span>
          <span
            @click="openInfoAgreement"
            class="float-end"
            v-if="agreementState.value && agreementData?.agreementType === 2"
          >
            <font-awesome-icon icon="fa-solid fa-circle-info" class="me-2 cursor-pointer" />
            <span class="me-3">Bono compra mínima:</span>
            <strong class="float-end" id="compraMinima">
              {{ $formatcurrency(agreementData?.agreement_minimum_purchase) }}
            </strong>
          </span>
        </p>
      </div>
    </div>

    <div class="col-4" :class="{ 'text-center': !isLoggedIn.value, 'text-end': isLoggedIn.value }">
      <NavbarOptionsList v-if="isLoggedIn.value" />
      <NavbarSignInPopover v-else class="me-5" />
    </div>
  </nav>
</template>

<script setup>
import SearchHelper from '@/components/shared/SearchHelperWithBarcode.vue'
import NavbarOptionsList from '@/components/layout/navigation/NavbarOptionsList.vue'
import { toRefs, computed, ref, onMounted } from 'vue'
import { useUserStore } from '@/stores/user'
import { useSiteConfigStore } from '@/stores/config/site'
import Swal from 'sweetalert2'
import NavbarSignInPopover from '@/components/layout/navigation/NavbarSignInPopover.vue'

const userStore = useUserStore()
const agreementData = computed(() => userStore.getAgreementData)
const siteConfigStore = useSiteConfigStore()
const canSelectOffice = computed(() => siteConfigStore.getCanSelectOffice)

const officeName = computed(() => userStore.getOfficeName)
const emit = defineEmits(['openSelectOffice'])

const props = defineProps({
  config: { type: Object, required: true }
})

const { config } = toRefs(props)

const brandName = import.meta.env.VITE_BRAND_NAME
const headerLogo = ref('')

// Al montar el componente, buscamos la imagen con el nombre y la extensión correcta
onMounted(async () => {
  const logoPath = await siteConfigStore.findImage('headerLogo')
  headerLogo.value = logoPath
})

const openInfoAgreement = () => {
  Swal.fire({
    title: 'Información Importante sobre el Bono en Productos Descatalogados',
    html: `
      <div class="text-left">
        <p>¡Buenas noticias! Esta sección especial de la tienda, identificada como <strong>"Bono"</strong>, te ofrece un beneficio adicional en tus compras.</p>
        <hr class="my-2">
        <p><strong>¿Cómo obtener el bono?</strong></p>
        <ul class="list-unstyled">
          <li class="mb-2">Debes incluir en tu carrito de compra <strong>únicamente productos</strong> que se encuentren dentro de la sección <strong>"Bono"</strong>.</li>
          <li>El total de tu compra de estos productos debe ser igual o superior a <strong>$${agreementData?.value.agreement_minimum_purchase}</strong>.</li>
        </ul>
        <hr class="my-2">
        <p><strong>Tu beneficio:</strong></p>
        <p>Si cumples con las condiciones anteriores, recibirás un <strong>bono</strong>.</p>
        <hr class="my-2">
        <p><strong>Importante:</strong></p>
        <ul class="list-unstyled">
          <li class="mb-2">Este bono se aplicará <strong>después de que completes tu compra</strong>.</li>
          <li class="mb-2">Una vez realizada tu compra, el bono será <strong>validado por nuestro equipo administrativo</strong>.</li>
        </ul>
        <hr class="my-2">
        <p class="text-center">¡Aprovecha este beneficio en los productos descatalogados!</p>
      </div>
    `,
    icon: 'info',
    width: '70vw',
    showCloseButton: true,
    showCancelButton: false,
    confirmButtonText: 'Entendido, ¡a comprar!',
    confirmButtonColor: '#f16322',
    cancelButtonText: 'Cancelar' // Aunque no haya acción de cancelar aquí, se puede dejar por consistencia
  })
}

// Ejemplo de cómo podrías llamar a la función:
// openInfoAgreement(50, 500); // Bono de $50 con un mínimo de compra de $500

const agreementState = computed(() => config.value.agreementState)
const isLoggedIn = computed(() => config.value.isLoggedIn)
const isRouteWithoutSearch = computed(() => config.value.isRouteWithoutSearch)
const userName = computed(() => config.value.userName.value)
const remainingBalance = computed(() => config.value.remainingBalance)
const search = config.value.search
const onlyTwoNames = computed(() => userName.value.split(' ').slice(0, 2).join(' '))
</script>

<style lang="scss" scoped></style>
